/*!
 * audioAnalysis.js -- Live Audio Input Analysis for Oscilla
 * Part of the Logic / Conditional system
 * © 2025 Rob Canning — GPLv3
 *
 * Captures audio input and extracts features, publishing them to ParamBus
 * for conditional triggering (if:) and for binding (link/map). Started from
 * a score by the adc() cue — named after Pd/Max's adc~.
 *
 * Published signals (under the adc uid, "adc" unless a cue renames it):
 *   - amp      : RMS amplitude (0-1)
 *   - peak     : Peak amplitude (0-1)
 *   - centroid : Spectral centroid, normalized (0-1, higher = brighter)
 *   - onset    : Onset detection flag (1 on attack, decays to 0)
 *   - low      : Low frequency energy (0-1)
 *   - mid      : Mid frequency energy (0-1)
 *   - high     : High frequency energy (0-1)
 *
 * Usage:
 *   import { startAudioAnalysis, stopAudioAnalysis } from './audioAnalysis.js';
 *   await startAudioAnalysis();
 *   // Now use if:adc.amp>0.5, or follow(adc.amp, …), in cues
 *   stopAudioAnalysis();
 */

import { sharedAudioCtx } from "../cues/audio/audioShared.js";
import { publish } from "./paramBinding.js";
import { parseChannel, channelLabel } from "./adcChannel.js";
import { pulse } from "./liveSignals.js";
import { subscribedPaths } from "./paramBus.js";
import { bandsWanted, addBandPowers, bandLevels } from "./spectrumBands.js";
import { detectPitch, MIN_PITCH } from "./pitchDetect.js";

// ===========================
// Module State
// ===========================
//
// ONE input stream, MANY taps. A tap is one analyser listening to one channel
// and publishing under one uid, so a stereo pair can drive two independent
// parts of a score:
//
//   adc(ch:l, uid:left)     left.amp, left.pitch, …
//   adc(ch:r, uid:right)    right.amp, right.pitch, …
//
// They share a single getUserMedia, so the performer is asked for the
// microphone once however many taps the score opens. Everything that carries
// frame-to-frame state — onset history, the smoothed amplitude and pitch —
// lives on the tap, because two channels must not share a decay envelope.

let micStream = null;
let micSource = null;
let splitter = null;          // built only when a tap asks for a real channel
let streamOpening = null;     // the input being opened: two adc() lines at once open it once
let inputUsers = 0;           // wave:in voices on the input; the taps are the others
let animFrameId = null;

/** uid -> tap. Empty means not running. */
const taps = new Map();
// why the input last failed to open, for adc() to say plainly (inputTrouble)
let lastStartError = null;

const ONSET_THRESHOLD = 0.15;  // Minimum amplitude jump to trigger onset
const ONSET_DECAY = 0.92;      // How quickly onset signal decays
const AMP_SMOOTHING = 0.3;     // 0 = no smoothing, 1 = frozen
const PITCH_CONFIDENCE_THRESHOLD = 0.9;  // Minimum correlation for valid pitch
const PITCH_SMOOTHING = 0.3;

// The band the spectral centroid is reported across, in Hz. Anything below LO
// reads 0, anything above HI reads 1; between them it is log-spaced.
const CENTROID_LO = 80;
const CENTROID_HI = 8000;
const LOG_CENTROID_LO = Math.log(CENTROID_LO);
const LOG_CENTROID_SPAN = Math.log(CENTROID_HI) - LOG_CENTROID_LO;

// The floor: a noise gate on each tap. What scales itself to the input's
// own loudest lately -- level, band(k, n), the input's scope -- blew a
// quiet room's hiss up to full size, and it jittered. Below the floor the
// input is silence for everything the tap measures and draws: a detector
// before the gate listens, and the gate opens on anything above the floor,
// and closes once the input has been FLOOR_HYST dB under it for FLOOR_HOLD_MS.
// adc(floor:-40) a higher one, floor:off none. <uid>.open: 1 while open.
const DEFAULT_FLOOR_DB = -50;
const FLOOR_HYST = 6;
const FLOOR_HOLD_MS = 200;

// level: amp over the loudest lately, which forgets over ~10 s (per frame, at 60 a second)
const LEVEL_PEAK_FALL = 0.9985;

// hit: an attack against the input's own recent level, so it comes at any
// gain -- unlike onset's fixed jump of 0.15, which a quiet input never makes.
// Its level jumping past HIT_RATIO times the last ~0.3 s's (HIT_SLOW_S),
// above a floor (-46 dB), is one hit; then none until it has fallen back
// under HIT_REARM times that, and not within HIT_GAP_MS of the last.
const HIT_RATIO = 2;
const HIT_REARM = 1.3;
const HIT_FLOOR = 0.005;
const HIT_SLOW_S = 0.3;
const HIT_GAP_MS = 60;

/** The measurements, all zero — sent on stop so nothing stays latched. */
const SILENCE = {
  amp: 0, peak: 0, centroid: 0, onset: 0, level: 0, open: 0,
  low: 0, mid: 0, high: 0, pitch: 0, note: 0, pitchConf: 0,
};


// ===========================
// Feature Extraction
// ===========================

/**
 * Calculate RMS amplitude from time domain data
 * @param {Uint8Array} data - Time domain data (0-255)
 * @returns {number} RMS amplitude (0-1)
 */
function calcRMS(data) {
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    const normalized = (data[i] - 128) / 128;  // Convert to -1 to 1
    sum += normalized * normalized;
  }
  return Math.sqrt(sum / data.length);
}

/**
 * Calculate peak amplitude from time domain data
 * @param {Uint8Array} data - Time domain data (0-255)
 * @returns {number} Peak amplitude (0-1)
 */
function calcPeak(data) {
  let peak = 0;
  for (let i = 0; i < data.length; i++) {
    const normalized = Math.abs(data[i] - 128) / 128;
    if (normalized > peak) peak = normalized;
  }
  return peak;
}

/**
 * Calculate spectral centroid (brightness indicator)
 * @param {Uint8Array} data - Frequency data (0-255)
 * @param {number} sampleRate - Audio sample rate
 * @returns {number} Normalized centroid (0-1)
 */
function calcCentroid(data, sampleRate) {
  const binWidth = (sampleRate / 2) / data.length;
  const loBin = Math.max(1, Math.floor(CENTROID_LO / binWidth));
  const hiBin = Math.min(data.length - 1, Math.ceil(CENTROID_HI / binWidth));

  // Band-limit, then subtract an adaptive floor.
  //
  // Measured on a real laptop mic in a quiet room: the full-band centroid sits
  // at ~11.8 kHz for ANY input, because getByteFrequencyData maps decibels onto
  // 0-255 and the noise floor reads ~80 in every bin up to nyquist. The old
  // `min(1, centroidHz / 8000)` therefore returned exactly 1.000 forever — an
  // outlet that looks like it works and never moves.
  //
  // Two things are needed, and neither alone is enough. Restricting to the band
  // anyone actually listens in brings it to ~3.5 kHz; subtracting the in-band
  // mean, which tracks the noise floor as the room changes, brings it to
  // ~1.7 kHz with real headroom in both directions. (Subtracting the MINIMUM
  // bin does nothing at all: some bin near nyquist always reads 0.)
  let sum = 0;
  for (let i = loBin; i <= hiBin; i++) sum += data[i];
  const floor = sum / (hiBin - loBin + 1);

  let weightedSum = 0;
  let magnitudeSum = 0;
  for (let i = loBin; i <= hiBin; i++) {
    const magnitude = data[i] - floor;
    if (magnitude <= 0) continue;
    weightedSum += magnitude * (i * binWidth);
    magnitudeSum += magnitude;
  }

  if (magnitudeSum === 0) return 0;
  const centroidHz = weightedSum / magnitudeSum;

  // Log spacing, because brightness is heard logarithmically: it puts the
  // interesting range across the middle of 0-1 rather than the bottom fifth.
  const t = (Math.log(Math.max(centroidHz, CENTROID_LO)) - LOG_CENTROID_LO) / LOG_CENTROID_SPAN;
  return Math.min(1, Math.max(0, t));
}

/**
 * Calculate energy in frequency bands
 * @param {Uint8Array} data - Frequency data (0-255)
 * @returns {Object} Band energies {low, mid, high} (0-1)
 */
function calcBands(data) {
  const len = data.length;
  // Three bands by fractions of the bins: low, mid, high. At 44.1 kHz
  // the edges fall near 440 Hz and 3.3 kHz (they move with the rate)
  const lowEnd = Math.floor(len * 0.02);
  const midEnd = Math.floor(len * 0.15);

  let lowSum = 0, midSum = 0, highSum = 0;

  for (let i = 0; i < len; i++) {
    const val = data[i] / 255;
    if (i < lowEnd) lowSum += val;
    else if (i < midEnd) midSum += val;
    else highSum += val;
  }

  return {
    low: lowSum / Math.max(1, lowEnd),
    mid: midSum / Math.max(1, midEnd - lowEnd),
    high: highSum / Math.max(1, len - midEnd)
  };
}

/**
 * Detect onset (attack transient)
 * @param {number} currentAmp - Current amplitude
 * @param {Object} tap - the tap whose decay envelope this is
 * @returns {number} Onset value (0-1)
 */
function detectOnset(currentAmp, tap) {
  const ampDelta = currentAmp - tap.prevAmp;

  // Trigger onset on significant amplitude increase
  if (ampDelta > ONSET_THRESHOLD && currentAmp > 0.1) {
    tap.onsetValue = 1;
  } else {
    // Decay onset signal
    tap.onsetValue *= ONSET_DECAY;
    if (tap.onsetValue < 0.01) tap.onsetValue = 0;
  }

  tap.prevAmp = currentAmp;
  return tap.onsetValue;
}

/**
 * Is this frame an attack (hit)? Against the tap's own slow level, so a
 * quiet input and a loud one hit alike; once per attack (it must fall back
 * to be armed again).
 * @param {number} amp  this frame's RMS
 * @param {Object} tap  keeps slow, armed, lastHit, lastT
 */
function detectHit(amp, tap) {
  const t = performance.now();
  const dt = Math.min(0.25, (t - (tap.lastT ?? t)) / 1000);
  tap.lastT = t;
  const slow = tap.slow ?? amp;
  let hit = false;
  if (tap.armed !== false && amp > HIT_FLOOR && amp > slow * HIT_RATIO && t - (tap.lastHit ?? -1e9) > HIT_GAP_MS) {
    hit = true;
    tap.armed = false;
    tap.lastHit = t;
  } else if (tap.armed === false && amp < slow * HIT_REARM) tap.armed = true;
  tap.slow = slow + (amp - slow) * (1 - Math.exp(-dt / HIT_SLOW_S));
  return hit;
}

/**
 * Convert frequency in Hz to MIDI note number
 * @param {number} hz - Frequency in Hz
 * @returns {number} MIDI note number (0 if invalid)
 */
function hzToMidi(hz) {
  if (hz <= 0) return 0;
  // MIDI note 69 = A4 = 440Hz
  return Math.round(12 * Math.log2(hz / 440) + 69);
}

// ===========================
// Analysis Loop
// ===========================

/**
 * The tap's gate, for this frame (the floor, above): open on anything over
 * the floor, closed after FLOOR_HOLD_MS under it by FLOOR_HYST dB. Glides
 * open in ~5 ms, shut in ~50 ms: no click. Returns whether it is open.
 */
function gateFrame(tap) {
  const floor = tap.floorDb;
  if (!Number.isFinite(floor)) { tap.gateOpen = true; tap.gate.gain.value = 1; return true; }
  tap.detector.getFloatTimeDomainData(tap.detectBuf);
  let sum = 0;
  for (let i = 0; i < tap.detectBuf.length; i++) sum += tap.detectBuf[i] * tap.detectBuf[i];
  const db = 20 * Math.log10(Math.sqrt(sum / tap.detectBuf.length) || 1e-12);
  const now = performance.now();
  let open = tap.gateOpen;
  if (db > floor) { open = true; tap.loudAt = now; }
  else if (db < floor - FLOOR_HYST && now - (tap.loudAt ?? -1e9) > FLOOR_HOLD_MS) open = false;
  if (open !== tap.gateOpen) {
    tap.gateOpen = open;
    tap.gate.gain.setTargetAtTime(open ? 1 : 0, sharedAudioCtx.currentTime, open ? 0.005 : 0.05);
  }
  return open;
}

/** Measure one tap's channel and publish its values under that tap's uid. */
function analyzeTap(tap) {
  const { analyser } = tap;
  const open = gateFrame(tap);
  analyser.getByteTimeDomainData(tap.timeDomainData);
  analyser.getByteFrequencyData(tap.frequencyData);
  analyser.getFloatTimeDomainData(tap.floatTimeDomainData);

  const rawAmp = calcRMS(tap.timeDomainData);
  tap.smoothedAmp = tap.smoothedAmp * AMP_SMOOTHING + rawAmp * (1 - AMP_SMOOTHING);
  // level: of its own loudest lately, as a sound's is (liveSignals.js), so
  // follow(adc.level, lo, hi) reaches hi however hot the input is
  tap.peakLately = Math.max(tap.smoothedAmp, (tap.peakLately || 0) * LEVEL_PEAK_FALL, 1e-4);
  const level = Math.min(1, tap.smoothedAmp / tap.peakLately);

  const peak = calcPeak(tap.timeDomainData);
  const centroid = calcCentroid(tap.frequencyData, sharedAudioCtx.sampleRate);
  const onset = detectOnset(rawAmp, tap);
  // an attack is a hit, as a synth's step is: <uid>.hit, .n, every(4, <uid>.hit)
  // -- for trig: and follow()
  if (detectHit(rawAmp, tap)) pulse(tap.uid, "adc");
  const bands = calcBands(tap.frequencyData);

  // Pitch (float data, for precision): about 2 ms a tap, so only while
  // something follows .pitch, .note or .pitchConf -- as the bands below
  const followed = subscribedPaths(`${tap.uid}.`);
  const wantsPitch = followed.some(p => /\.(pitch|note|pitchConf)$/.test(p));
  const { pitch: rawPitch, confidence: pitchConf } = wantsPitch
    ? detectPitch(tap.floatTimeDomainData, sharedAudioCtx.sampleRate)
    : { pitch: 0, confidence: 0 };

  // Smooth pitch (only when confident, otherwise decay)
  if (rawPitch > 0 && pitchConf > PITCH_CONFIDENCE_THRESHOLD) {
    tap.smoothedPitch = tap.smoothedPitch * PITCH_SMOOTHING + rawPitch * (1 - PITCH_SMOOTHING);
  } else {
    tap.smoothedPitch *= 0.95;  // Decay when no pitch detected
    if (tap.smoothedPitch < MIN_PITCH) tap.smoothedPitch = 0;
  }

  // band(k, n), while something follows one: adc.band(2, 4) -- each band
  // against its own loudest lately (spectrumBands.js), so at any gain
  let byBand = null;
  const wanted = bandsWanted(followed, `${tap.uid}.`);
  const ns = Object.keys(wanted);
  if (ns.length) {
    if (tap.floatFreq?.length !== analyser.frequencyBinCount) tap.floatFreq = new Float32Array(analyser.frequencyBinCount);
    analyser.getFloatFrequencyData(tap.floatFreq);
    tap.bandState ||= {};
    for (const n of ns) {
      const sum = addBandPowers(new Float64Array(Number(n)), tap.floatFreq, sharedAudioCtx.sampleRate, Number(n));
      // per frame (60 a second): the sounds' rates (liveSignals, 30 a second) halved
      byBand = { ...byBand, ...bandLevels(sum, Number(n), wanted[n], tap.bandState[n] ||= {}, { fall: 0.9985, release: 0.92 }) };
    }
  }

  publish("adc", tap.uid, {
    ...byBand,
    amp: tap.smoothedAmp,
    peak,
    centroid,
    onset,
    level,
    low: bands.low,
    mid: bands.mid,
    high: bands.high,
    pitch: tap.smoothedPitch,
    note: hzToMidi(tap.smoothedPitch),
    pitchConf,
    open: open ? 1 : 0,
  }, { source: 'audio-analysis' });
}

function analyzeFrame() {
  if (taps.size === 0) return;
  for (const tap of taps.values()) analyzeTap(tap);
  animFrameId = requestAnimationFrame(analyzeFrame);
}

// ===========================
// Public API
// ===========================

export { parseChannel } from "./adcChannel.js";

/**
 * The uid the measurements publish under when a cue does not say. "adc" after
 * Pd/Max's adc~, so the word means the same thing here as in the tools people
 * arrive from.
 */
let analysisUid = "adc";
export function setAnalysisUid(uid) {
  if (uid && typeof uid === "string") analysisUid = uid;
}
export function getAnalysisUid() { return analysisUid; }

/**
 * Open the input once, shared by every tap.
 *
 * channelCount is asked for as `ideal`, not required: a built-in laptop mic is
 * mono and would refuse a hard constraint outright, which would be a worse
 * outcome than analysing one channel twice.
 */
async function ensureStream() {
  if (micStream) return true;
  streamOpening ??= (async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: { ideal: 2 },
      },
    });
    if (sharedAudioCtx.state === "suspended") await sharedAudioCtx.resume();
    micStream = stream;
    micSource = sharedAudioCtx.createMediaStreamSource(stream);
    return true;
  })().finally(() => { streamOpening = null; });
  return streamOpening;
}

/** The input closed (the browser's microphone light off) once no tap and no wave:in voice uses it. */
function closeStreamIfUnused() {
  if (taps.size || inputUsers) return;
  if (splitter) { try { splitter.disconnect(); } catch { /* */ } splitter = null; }
  if (micSource) { micSource.disconnect(); micSource = null; }
  if (micStream) {
    micStream.getTracks().forEach((track) => track.stop());
    micStream = null;
  }
}

/**
 * The input as a sound, for a synth's wave:in (synth.js, sources.js): the
 * same one adc() listens to, opened if it is not. Null when it cannot be
 * (inputTrouble() says why).
 */
export async function inputSourceNode() {
  try {
    await ensureStream();
    lastStartError = null;
    inputUsers++;               // given back by releaseInputSource when the voice ends
    return micSource;
  } catch (err) {
    lastStartError = err;
    return null;
  }
}

/** A wave:in voice is done with the input (inputSourceNode). */
export function releaseInputSource() {
  inputUsers = Math.max(0, inputUsers - 1);
  closeStreamIfUnused();
}

/** How many channels the device actually gave us, whatever we asked for. */
export function getInputChannelCount() {
  const track = micStream?.getAudioTracks?.()[0];
  return track?.getSettings?.().channelCount ?? (micStream ? 1 : 0);
}

/** Build one analyser listening to `channel`, and wire it to the input. */
function createTap(uid, channel, gainValue = 1, floorDb = DEFAULT_FLOOR_DB) {
  const analyser = sharedAudioCtx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.3;
  // gain: ahead of everything the tap does -- what it measures, its onsets'
  // threshold, its scope (adc(gain:4) for a quiet input); then the floor's
  // gate (gateFrame), heard by a detector of its own before it
  const gain = sharedAudioCtx.createGain();
  gain.gain.value = gainValue;
  const detector = sharedAudioCtx.createAnalyser();
  detector.fftSize = 1024;
  gain.connect(detector);
  const gate = sharedAudioCtx.createGain();
  gate.gain.value = Number.isFinite(floorDb) ? 0 : 1;      // shut until something is heard
  gain.connect(gate);
  gate.connect(analyser);

  if (channel === "mix") {
    // No splitter: the analyser down-mixes the input itself.
    micSource.connect(gain);
  } else {
    if (!splitter) {
      splitter = sharedAudioCtx.createChannelSplitter(2);
      micSource.connect(splitter);
    }
    const available = getInputChannelCount();
    const idx = channel < available ? channel : 0;
    if (idx !== channel) {
      console.warn(`[audioAnalysis] ${uid}: ch:${channel} requested but the input ` +
                   `has ${available} channel(s) — listening to channel 0 instead.`);
    }
    splitter.connect(gain, idx);
  }
  // Nothing connects onward to the destination: this only listens.

  return {
    uid, channel, analyser, gain, detector, gate, input: [gate, 0], scope: null,
    floorDb, gateOpen: !Number.isFinite(floorDb), detectBuf: new Float32Array(detector.fftSize),
    timeDomainData: new Uint8Array(analyser.fftSize),
    frequencyData: new Uint8Array(analyser.frequencyBinCount),
    floatTimeDomainData: new Float32Array(analyser.fftSize),
    prevAmp: 0, onsetValue: 0, smoothedAmp: 0, smoothedPitch: 0,
  };
}

/**
 * Start analysing, or add another channel tap.
 *
 * @param {Object} [opts]
 * @param {string} [opts.uid]  publish under this uid (default: the module uid)
 * @param {string|number} [opts.ch]  "l" | "r" | "mix" | an index (default "mix")
 * @returns {Promise<boolean>} true if this tap is now running
 */
export async function startAudioAnalysis(opts = {}) {
  const uid = (typeof opts.uid === "string" && opts.uid) ? opts.uid : analysisUid;
  const channel = parseChannel(opts.ch);

  const existing = taps.get(uid);
  if (existing && existing.channel === channel) return true;
  const gainValue = existing ? existing.gain.gain.value : 1;
  const floorDb = existing ? existing.floorDb : DEFAULT_FLOOR_DB;

  try {
    await ensureStream();
    // Re-pointing an existing uid at a different channel replaces its tap
    // rather than leaving two analysers publishing to one path.
    if (existing) removeTap(uid);

    const wasIdle = taps.size === 0;
    taps.set(uid, createTap(uid, channel, gainValue, floorDb));
    lastStartError = null;
    if (wasIdle) analyzeFrame();

    const where = channelLabel(channel);
    console.log(`[audioAnalysis] Started — ${where}, publishing to ${uid}.*`);
    window.dispatchEvent(new CustomEvent("oscilla:audioAnalysis", {
      detail: { state: "started", uid, channel },
    }));
    return true;
  } catch (err) {
    lastStartError = err;
    if (taps.size === 0) stopAudioAnalysis();
    return false;
  }
}

/** Disconnect one tap and zero the signals it was publishing. */
function removeTap(uid) {
  const tap = taps.get(uid);
  if (!tap) return;
  try { tap.analyser.disconnect(); } catch { /* already gone */ }
  try { tap.gain.disconnect(); } catch { /* already gone */ }
  try { tap.gate.disconnect(); } catch { /* already gone */ }
  try { tap.scope?.disconnect(); } catch { /* already gone */ }
  taps.delete(uid);
  publish("adc", uid, { ...SILENCE }, { source: 'audio-analysis' });
}

/**
 * Stop one tap, or — with no uid — every tap, releasing the microphone.
 * @param {string} [uid]
 */
export function stopAudioAnalysis(uid) {
  if (uid) {
    removeTap(uid);
    if (taps.size > 0) return;      // other channels are still listening
  } else {
    for (const id of [...taps.keys()]) removeTap(id);
  }

  if (animFrameId) {
    cancelAnimationFrame(animFrameId);
    animFrameId = null;
  }
  // the input stays open while a wave:in voice still plays it
  closeStreamIfUnused();

  console.log("[audioAnalysis] Stopped");
  window.dispatchEvent(new CustomEvent("oscilla:audioAnalysis", {
    detail: { state: "stopped" },
  }));
}

/**
 * A tap's gain, gliding there in ~50 ms (no click). False: no such tap.
 * @param {string} uid
 * @param {number} value  1 as it comes, 4 four times as loud, 0 silent
 */
export function setTapGain(uid, value) {
  const tap = taps.get(uid);
  const g = Math.max(0, Number(value));
  if (!tap || !Number.isFinite(g)) return false;
  tap.gain.gain.setTargetAtTime(g, sharedAudioCtx.currentTime, 0.015);
  return true;
}

/**
 * A tap's floor, in dB (the noise gate, above): -50 by default, higher to
 * shut out more, -Infinity (floor:off) for none. False: no such tap.
 */
export function setTapFloor(uid, db) {
  const tap = taps.get(uid);
  if (!tap) return false;
  tap.floorDb = Number(db);
  if (!Number.isFinite(tap.floorDb)) tap.floorDb = -Infinity;
  return true;
}

/**
 * Why the input did not open, in words a performer can act on -- or null.
 * The browser shows no prompt when there is nothing to ask for: with no
 * input device at all (a sound card on an output-only profile, an interface
 * unplugged) getUserMedia fails at once, NotFoundError, and no dialog ever
 * appears; that is the confusing one.
 */
export function inputTrouble() {
  const e = lastStartError;
  if (!e) return null;
  if (!window.isSecureContext) return "the page is not secure (http on another machine): the browser gives no input there -- open it over https, or on localhost";
  switch (e.name) {
    case "NotFoundError": return "no audio input to open: the system offers none -- plug one in, or turn one on in the sound settings (on Linux a card can be on an output-only profile)";
    case "NotAllowedError": return "the browser was refused the input: allow the microphone for this page (the icon at the left of the address bar)";
    case "NotReadableError": return "the input is in use or will not start: another program may hold it";
    default: return `the input did not open: ${e.message || e.name}`;
  }
}

/**
 * An analyser of its own on a tap's input, for drawing it (adc.js: the
 * input's box, a scope like a synth's). Not the tap's: a scope sets its
 * analyser's smoothing to suit its view, which would change what the tap
 * measures. Made on first asking; goes with the tap. Null: no such tap.
 * @param {string} uid
 * @returns {AnalyserNode|null}
 */
export function tapScopeAnalyser(uid) {
  const tap = taps.get(uid);
  if (!tap) return null;
  if (!tap.scope) {
    tap.scope = sharedAudioCtx.createAnalyser();
    tap.scope.fftSize = 2048;
    const [node, output = 0] = tap.input;
    node.connect(tap.scope, output);
  }
  return tap.scope;
}

/**
 * Is anything listening — or, with a uid, is that particular tap?
 * @param {string} [uid]
 * @returns {boolean}
 */
export function isAudioAnalysisRunning(uid) {
  return uid ? taps.has(uid) : taps.size > 0;
}

/** What is listening right now, for the live console and the connections view. */
export function listAudioTaps() {
  return [...taps.values()].map(({ uid, channel }) => ({ uid, channel }));
}

/**
 * Toggle audio analysis on/off
 * @returns {Promise<boolean>} New running state
 */
export async function toggleAudioAnalysis() {
  if (taps.size > 0) {
    stopAudioAnalysis();
    return false;
  }
  return await startAudioAnalysis();
}

// Expose on window for debugging
window.oscillaAudioAnalysis = {
  start: startAudioAnalysis,
  stop: stopAudioAnalysis,
  toggle: toggleAudioAnalysis,
  isRunning: isAudioAnalysisRunning,
  taps: listAudioTaps,
  channels: getInputChannelCount,
};
